import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Product } from "../types";
import { Handshake, LayoutGrid, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { addEventAttributes } from "../ui-script/utils";

export interface RecommendationProps {
  complementartLst: Record<string, Category>;
  complementaryStyles: Record<
    string,
    {
      position: string;
      results: Product[];
      subcategory: string;
    }[]
  >;
  loading: boolean;
  similarLst: any;
  type?: "recommended" | "complete-the-look";
  currentProductHandle?: string;
  showComplementaryGrid?: boolean;
  hideTypeSelectionTab?: boolean;
  showAddToCartButton?: boolean;
  onAddToCart?: (handle: string) => void;
  defaultSelected?: boolean;
  currentProductPosition: string;
  lowTicketSize?: any;
}

function RecommendationWithoutDrawer(props: RecommendationProps) {
  const [showSimilar, setShowSimilar] = useState(false);
  const [animationInProgress, setAnimationInProgress] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showScrollText, setShowScrollText] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  const [showButtons, setShowButtons] = useState<number[]>([]);
  useEffect(() => {
    if (showButtons.length > 3 || !showSimilar) return;
    const timeout = setTimeout(() => {
      setShowButtons([...showButtons, showButtons.length]);
    }, 200);
    return () => clearTimeout(timeout);
  }, [showButtons, showSimilar]);

  useEffect(() => {
    if (hasInteracted) {
      if (!showSimilar) {
        setAnimationInProgress(true);
      }
    }
  }, [showSimilar, hasInteracted]);

  // Add scroll event listener with debounce
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);

      // Check if scrolled 50% of viewport height
      if (scrollDelta >= window.innerHeight * 0.5) {
        // Clear any existing timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }

        // Show text immediately when scrolling
        setShowScrollText(true);

        // Set a new timeout to hide the text after scrolling stops
        const timeout = setTimeout(() => {
          setShowScrollText(false);
        }, 2000); // Hide after 2 seconds of no scrolling

        setScrollTimeout(timeout);

        // Update last scroll position
        lastScrollY = currentScrollY;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [scrollTimeout]);

  const handleToggleSimilar = () => {
    setShowSimilar(!showSimilar);
    setHasInteracted(true);
  };

  const hasComplementary = useMemo(() => {
    return Object.keys(props.complementaryStyles ?? {}).length > 0;
  }, [props.complementaryStyles]);

  const product = window.helium.datastore.current.product.details;

  return (
    <>
      <button
        className={cn(
          "hp-transition-all hp-ease-in-out hp-flex hp-items-center hp-justify-center hp-duration-1000",
          "hp-bg-black hp-hover:bg-gray-800 hp-text-white hp-font-semibold hp-fixed hp-bottom-36 hp-right-4 hp-rounded-full hp-shadow-lg",
          showSimilar ? "-hp-translate-y-2" : "hp-translate-y-0",
          {
            "hp-px-4": showScrollText,
          },
        )}
        style={{
          zIndex: 99,
          bottom:
            window.heliumComponenConfigs?.curated_picks?.styles?.root?.bottom ??
            "150px",
          right: "20px",
          width: "auto",
          borderRadius: "16px",
          minWidth: "56px",
          height: "56px",
          background: "var(--brand-color-primary)",
          transitionDuration: "1000ms",
          ...window.heliumComponenConfigs?.curated_picks?.styles?.root,
        }}
        onClick={() => {
          handleToggleSimilar();
        }}
        {...addEventAttributes({
          eventName: "button_click",
          componentType: "curated_picks",
          eventData: {
            productId: props.currentProductHandle,
          },
        })}
        aria-label={showSimilar ? "Close similar items" : "Show similar items"}
      >
        <div className={cn("hp-relative")}>
          <span
            className={cn(
              "hp-inset-0 hp-transition-all hp-w-full hp-flex hp-items-center hp-justify-center",
              showSimilar
                ? "hp-opacity-0 -hp-translate-y-3"
                : "hp-opacity-100 hp-translate-y-0",
            )}
            style={{
              transitionDuration: "1000ms",
            }}
          >
            <img
              src="https://cdn.shopify.com/s/files/1/0795/9858/4101/files/shopoinglook.png?v=1744803698"
              className="hp-w-[4rem]"
            />
            <span
              className={cn(
                "hp-whitespace-nowrap hp-transition-all hp-duration-1000 hp-overflow-hidden",
                {
                  "hp-w-fit hp-opacity-100 hp-text-lg": showScrollText,
                  "hp-w-0 hp-opacity-0": !showScrollText,
                },
              )}
              style={{
                transitionDuration: "1000ms",
                transition: "width 1s",
                width: showScrollText ? "86px" : 0,
              }}
            >
              Curated picks
            </span>
          </span>
          <span
            className={cn(
              "hp-absolute hp-inset-0 hp-transition-all hp-duration-300 hp-flex hp-items-center hp-justify-center",
              showSimilar
                ? "hp-opacity-100 hp-translate-y-0"
                : "hp-opacity-0 hp-translate-y-3",
            )}
          >
            <X className="hp-h-8 hp-w-8" color="white" />
          </span>
        </div>
      </button>
      {!props.hideTypeSelectionTab && (showSimilar || animationInProgress) && (
        <div
          className={cn(
            "hp-flex hp-fixed hp-gap-y-1 hp-bottom-56 hp-right-4 hp-flex-col hp-items-end hp-px-4 hp-b-4",
          )}
          style={{
            zIndex: 99,
            marginBottom: "90px",
          }}
        >
          {
            <button
              className={cn(
                "hp-py-4 hp-px-8 fade-up-item hp-items-center hp-flex hp-cursor-pointer hp-min-w-0 hp-gap-x-4 hp-rounded-xl hp-bg-white hp-shadow-lg hp-bg-neutral-50",
                showSimilar ? "fade-up-item" : "fade-down-item",
              )}
              {...addEventAttributes({
                eventName: "button_click",
                componentType: "similar",
                eventData: {
                  productId: props.currentProductHandle,
                },
              })}
              onClick={() => {
                props?.sdk?.renderButtonDrawer({
                  ...props,
                  s: props.similarLst,
                  c: props.complementartLst,
                  type: "recommended",
                });
                setShowSimilar(false);
              }}
              style={{
                animationDelay: showSimilar
                  ? `${150 * 3}ms`
                  : `${750 - 150 * 3}ms`,
              }}
            >
              <div className="hp-h-14 hp-w-14 hp-bg-gray-50 hp-rounded-xl hp-p-0.5 hp-flex hp-justify-center hp-items-center hp-overflow-hidden">
                <img
                  src="https://static.vecteezy.com/system/resources/thumbnails/015/645/433/small/view-similar-products-icon-in-line-style-design-isolated-on-white-background-editable-stroke-free-vector.jpg"
                  alt="better-together"
                  className="hp-w-10 hp-w-10 hp-object-contain"
                />
              </div>
              <div className="min-w-0 flex-auto">
                <p className="hp-text-xl hp-font-semibold hp-text-black">
                  Similar products
                </p>
              </div>
            </button>
          }

          {!!props.lowTicketSize && (showSimilar || animationInProgress) && (
            <div
              className={cn(
                "hp-py-4 hp-px-8 hp-items-center hp-flex hp-cursor-pointer hp-min-w-0 hp-gap-x-4 hp-rounded-xl hp-bg-white hp-shadow-xl hp-bg-neutral-50 hp-w-full",
                showSimilar ? "fade-up-item" : "fade-down-item",
                {
                  ...addEventAttributes({
                    eventName: "button_click",
                    componentType: "low_ticket",
                    eventData: {
                      productId: props.currentProductHandle,
                    },
                  }),
                },
              )}
              onClick={() => {
                props?.sdk?.renderButtonDrawer({
                  ...props,
                  s: props.similarLst,
                  c: props.complementartLst,
                  type: "low-ticket-size",
                });
                setShowSimilar(false);
              }}
              style={{
                animationDelay: `${150 * 2}ms`,
              }}
            >
              <div className="hp-h-14 hp-w-14 hp-bg-gray-50 hp-rounded-xl hp-p-0.5 hp-flex hp-justify-center hp-items-center hp-overflow-hidden">
                <img
                  src="https://cdn.shopify.com/s/files/1/0795/9858/4101/files/dea.png?v=1744282098"
                  alt="better-together"
                  className="hp-w-10 hp-w-10 hp-object-contain"
                />
              </div>
              <div className="" style={{ width: "170px" }}>
                <p className="hp-text-xl hp-font-semibold hp-text-black">
                  Steal Deals with
                </p>
                <span className="hp-truncate hp-block hp-text-lg">
                  {product.title}
                </span>
              </div>
            </div>
          )}

          {hasComplementary &&
            !window.heliumConfig?.features?.better_together?.disabled &&
            (showSimilar || animationInProgress) && (
              <div
                className={cn(
                  "hp-py-4 hp-px-8 hp-items-center hp-cursor-pointer hp-flex hp-min-w-0 hp-gap-x-4 hp-rounded-xl hp-bg-white hp-shadow-xl hp-bg-neutral-50 hp-w-full",
                  showSimilar ? "fade-up-item" : "fade-down-item",
                )}
                {...addEventAttributes({
                  eventName: "button_click",
                  componentType: "complementary",
                  eventData: {
                    productId: props.currentProductHandle,
                  },
                })}
                onClick={() => {
                  props?.sdk?.renderButtonDrawer({
                    ...props,
                    s: props.similarLst,
                    c: props.complementartLst,
                    type: "complete-the-look",
                  });
                  setShowSimilar(false);
                }}
                onAnimationEnd={() => {
                  if (!showSimilar) {
                    setAnimationInProgress(false);
                  }
                }}
                style={{
                  animationDelay: showSimilar
                    ? `${150 * 1}ms`
                    : `${750 - 150 * 2}ms`,
                }}
              >
                <div className="hp-h-14 hp-w-14 hp-bg-gray-50 hp-rounded-xl hp-p-0.5 hp-flex hp-justify-center hp-items-center hp-overflow-hidden">
                  <img
                    src="https://png.pngtree.com/png-vector/20230527/ourmid/pngtree-clothes-icon-vector-image-concept-dress-outline-vector-png-image_52306436.jpg"
                    alt="better-together"
                    className="hp-w-10 hp-w-10 hp-object-contain"
                  />
                </div>
                <div className="" style={{ width: "170px" }}>
                  <p className="hp-text-xl hp-font-semibold hp-text-black">
                    Best paired with
                  </p>
                  <span className="hp-truncate hp-block hp-text-lg">
                    {product.title}
                  </span>
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
}

export default RecommendationWithoutDrawer;
